import React, { useMemo, useEffect, useState } from 'react'
import { Box, Typography, Paper, Alert, Divider, Fade } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { useDropZoneContext } from './DropZone'
import { Table } from 'Common/MUITable/MUITable'
import { GridColDef } from '@mui/x-data-grid'
import { dataTransformationUtils } from './constants'

// Using the shared transformDataUsingMapping function from DropZone.tsx
const {
  transformDataUsingMapping,
  generateDifferenceData,
  generateAddedRows,
  detectDuplicatePredicates,
  detectEmptyPredicates,
} = dataTransformationUtils

interface UploadTabContentProps {
  handleModalClose: () => void
  handleUploadClick: () => void
  isUploading?: boolean
  mappingRows?: Array<Record<string, unknown>>
  onValidationChange?: (isValid: boolean) => void
}

export const UploadTabContent: React.FC<UploadTabContentProps> = ({
  handleModalClose,
  handleUploadClick: _handleUploadClick,
  isUploading,
  mappingRows = [],
  onValidationChange,
}) => {
  // Using the context to access the upload status and other data
  const context = useDropZoneContext()
  const [diffData, setDiffData] = useState<Array<Record<string, unknown>>>([])
  const [addedData, setAddedData] = useState<Array<Record<string, unknown>>>([])
  const [duplicatePredicates, setDuplicatePredicates] = useState<{
    duplicates: Array<{
      key: string
      count: number
      rows: Array<Record<string, unknown>>
    }>
    hasDuplicates: boolean
  }>({ duplicates: [], hasDuplicates: false })

  const [emptyPredicates, setEmptyPredicates] = useState<{
    emptyPredicates: Array<{
      rowIndex: number
      row: Record<string, unknown>
      emptyColumns: string[]
    }>
    hasEmptyPredicates: boolean
  }>({ emptyPredicates: [], hasEmptyPredicates: false })

  // Extract and memoize values from context to prevent re-renders
  const contextTableData = useMemo(() => context.tableData, [context.tableData])
  const contextOriginalData = useMemo(
    () => context.originalData,
    [context.originalData]
  )
  const contextPredicate = useMemo(
    () => context.predicate ?? [],
    [context.predicate]
  )
  const contextColumns = useMemo(() => context.columns, [context.columns])
  const contextUpdateRowsStatus = useMemo(
    () => context.updateRowsStatus,
    [context.updateRowsStatus]
  )
  const contextDataConfig = useMemo(
    () => context.dataConfig,
    [context.dataConfig]
  )

  // Determine if we have status data to show
  const hasUpdateStatus = useMemo(
    () =>
      Boolean(
        contextUpdateRowsStatus &&
          Object.keys(contextUpdateRowsStatus).length > 0
      ),
    [contextUpdateRowsStatus]
  )

  // Upload is completed if we have updateRowsStatus and we're not in uploading state
  const isUploadComplete = useMemo(
    () => hasUpdateStatus && !isUploading,
    [hasUpdateStatus, isUploading]
  )

  // Validation logic for required columns, row count, and duplicate predicates
  const { missingRequiredColumns, isValidForUpload, rowCountWarning } =
    useMemo(() => {
      let missingRequired: string[] = []
      let isRequiredValid = true
      let rowWarning = ''

      // Check required column mappings
      if (
        contextDataConfig &&
        contextDataConfig.required &&
        contextDataConfig.required.length > 0
      ) {
        const requiredColumns = contextDataConfig.required
        const mappingMap = new Map(
          mappingRows.map((row) => [row.target as string, row.source as string])
        )

        // Find required columns that are either not mapped or mapped to null/empty
        missingRequired = requiredColumns.filter((reqCol) => {
          const mappedSource = mappingMap.get(reqCol)
          return !mappedSource || mappedSource === null || mappedSource === ''
        })

        isRequiredValid = missingRequired.length === 0
      }

      // Check row count difference between uploaded file and original data
      if (
        contextOriginalData &&
        contextOriginalData.length > 0 &&
        contextTableData &&
        contextTableData.length > 0
      ) {
        const originalRowCount = contextOriginalData.length
        const uploadedRowCount = contextTableData.length

        if (uploadedRowCount < originalRowCount) {
          rowWarning = `Your uploaded file has ${uploadedRowCount} rows, but the original data has ${originalRowCount} rows. You may be missing ${originalRowCount - uploadedRowCount} rows of data.`
        }
      }

      // Include duplicate predicate and empty predicate checks in validation
      const hasValidation =
        isRequiredValid &&
        !duplicatePredicates.hasDuplicates &&
        !emptyPredicates.hasEmptyPredicates

      return {
        missingRequiredColumns: missingRequired,
        isValidForUpload: hasValidation,
        rowCountWarning: rowWarning,
      }
    }, [
      contextDataConfig,
      mappingRows,
      contextOriginalData,
      contextTableData,
      duplicatePredicates.hasDuplicates,
      emptyPredicates.hasEmptyPredicates,
    ])

  // Notify parent component about validation state changes
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValidForUpload)
    }
  }, [isValidForUpload, onValidationChange])

  // Using the already defined memoized context values

  // Smart comparison: Identify columns that exist in transformed data
  const transformedDataColumns = useMemo(() => {
    if (!mappingRows || mappingRows.length === 0) {
      return []
    }

    // Get successfully mapped columns (ones with non-null source mappings)
    const successfullyMappedColumns = mappingRows
      .filter(
        (row) =>
          row.source !== null && row.source !== undefined && row.source !== ''
      )
      .map((row) => row.target as string)

    console.log(
      'Successfully mapped columns for comparison:',
      successfullyMappedColumns
    )
    return successfullyMappedColumns
  }, [mappingRows])

  // Generate transformed data when the component mounts or when relevant data changes
  useEffect(() => {
    if (contextTableData && contextTableData.length > 0) {
      // Transform the data using the mapping
      const transformed = transformDataUsingMapping(
        contextTableData,
        mappingRows
      )

      // Check for duplicate predicates in transformed data
      const smartPredicateColumns = (contextPredicate ?? []).filter((col) =>
        transformedDataColumns.includes(col)
      )

      if (smartPredicateColumns.length > 0) {
        const duplicateResult = detectDuplicatePredicates(
          transformed,
          smartPredicateColumns
        )
        setDuplicatePredicates(duplicateResult)

        if (duplicateResult.hasDuplicates) {
          console.warn(
            'Duplicate predicates detected:',
            duplicateResult.duplicates
          )
        }

        // Check for empty predicate values in transformed data
        const emptyPredicateResult = detectEmptyPredicates(
          transformed,
          smartPredicateColumns
        )
        setEmptyPredicates(emptyPredicateResult)

        if (emptyPredicateResult.hasEmptyPredicates) {
          console.warn(
            'Empty predicate values detected:',
            emptyPredicateResult.emptyPredicates
          )
        }
      }

      // Generate difference data between original and transformed
      if (contextOriginalData && contextOriginalData.length > 0) {
        // Smart filtering: Only use columns that exist in transformed data
        const smartEditableColumns = transformedDataColumns.filter((col) => {
          // Include if it's in the successfully mapped columns
          return transformedDataColumns.includes(col)
        })

        console.log(
          'Smart editable columns for comparison:',
          smartEditableColumns
        )

        console.log(
          'Smart predicate columns for comparison:',
          smartPredicateColumns
        )

        // Generate differences only for successfully mapped columns
        const differences = generateDifferenceData(
          transformed,
          contextOriginalData,
          smartPredicateColumns,
          smartEditableColumns
        )
        setDiffData(differences)

        const newlyAddedData = generateAddedRows(
          transformed,
          contextOriginalData,
          smartPredicateColumns
        )
        setAddedData(newlyAddedData)
        console.log(
          'Generated additional rows with smart filtering:',
          newlyAddedData
        )
      }
    }
  }, [
    contextTableData,
    contextOriginalData,
    contextPredicate,
    contextColumns,
    mappingRows,
    transformedDataColumns,
    // transformDataUsingMapping and generateDifferenceData are now external functions,
    // so they don't need to be dependencies
  ])

  // Memoize difference table columns
  const diffColumns = useMemo((): GridColDef[] => {
    const columns: GridColDef[] = []

    // Always ensure ALL predicate columns from context are included, even if data is empty
    const predicateColumns = contextPredicate ?? []

    // First add ALL predicate columns from context regardless of data existence
    // This ensures columns like productCode are always shown even if not in the transformed data
    predicateColumns.forEach((column) => {
      columns.push({
        field: column,
        headerName: `${column} (ID)`,
        flex: 1,
        cellClassName: () => 'predicate-cell',
        // Add valueGetter with proper null checking using optional chaining
      })
    })

    // If we have diff data, extract column information for change columns
    if (diffData.length > 0) {
      try {
        // Get all keys from the first row except id, with safety checks
        const firstRow = diffData[0] || {}
        const keys = Object.keys(firstRow).filter((k) => k !== 'id')

        // Separate existing predicate columns from change columns in diffData
        const existingPredicateColumns = keys.filter(
          (key) => !key.includes('_')
        )
        const changeColumns = keys.filter((key) => key.includes('_'))

        // Add any additional existing predicate columns not already included
        existingPredicateColumns.forEach((column) => {
          // Only add if not already included
          if (
            !predicateColumns.includes(column) &&
            !columns.some((col) => col.field === column)
          ) {
            columns.push({
              field: column,
              headerName: `${column} (ID)`,
              flex: 1,
              cellClassName: () => 'predicate-cell',
              // Add valueGetter with proper null checking using optional chaining
            })
          }
        })

        // Group change columns by their base column name (_old, _new, _changed suffixes)
        const columnGroups = changeColumns.reduce(
          (groups: Record<string, string[]>, key) => {
            // Safety check for malformed keys using optional chaining
            if (!key?.includes('_')) return groups

            // Extract base column name by removing suffix (_old, _new, _changed)
            const parts = key.split('_')
            if (parts.length < 2) return groups

            const baseColumn = parts[0]
            if (!baseColumn) return groups

            if (!groups[baseColumn]) {
              groups[baseColumn] = []
            }
            groups[baseColumn].push(key)
            return groups
          },
          {} as Record<string, string[]>
        )

        // Create columns for each base column with changes
        Object.keys(columnGroups).forEach((baseColumn) => {
          // Skip if this doesn't have both old and new values
          if (
            !columnGroups[baseColumn].includes(`${baseColumn}_old`) ||
            !columnGroups[baseColumn].includes(`${baseColumn}_new`)
          ) {
            return
          }

          // Add old value column
          columns.push({
            field: `${baseColumn}_old`,
            headerName: `${baseColumn} (Original)`,
            flex: 1,
            cellClassName: () => 'old-value-cell',
            // Add valueGetter with proper null checking using optional chaining
          })

          // Add new value column
          columns.push({
            field: `${baseColumn}_new`,
            headerName: `${baseColumn} (New)`,
            flex: 1,
            cellClassName: () => 'new-value-cell',
            // Add valueGetter with proper null checking using optional chaining
          })
        })
      } catch (error) {
        console.error('Error processing diffData for columns:', error)
      }
    }

    return columns
  }, [diffData, contextPredicate])

  // Memoize added data table columns
  const addedColumnsData = useMemo((): GridColDef[] => {
    const columns: GridColDef[] = []

    if (addedData.length > 0) {
      try {
        // Get all keys from the first row
        const firstRow = addedData[0] || {}
        const keys = Object.keys(firstRow).filter((k) => k !== 'id')

        // First add predicate columns (usually used for identification)
        const predicateColumns = contextPredicate ?? []

        predicateColumns.forEach((column) => {
          if (keys.includes(column)) {
            columns.push({
              field: column,
              headerName: `${column} (ID)`,
              flex: 1,
              cellClassName: () => 'predicate-cell',
            })
          }
        })

        // Then add all other columns that aren't predicates
        keys
          .filter((key) => !predicateColumns.includes(key))
          .forEach((column) => {
            columns.push({
              field: column,
              headerName: column,
              flex: 1,
              cellClassName: () => 'added-value-cell',
            })
          })
      } catch (error) {
        console.error('Error processing addedData for columns:', error)
      }
    }

    return columns
  }, [addedData, contextPredicate])

  // Memoize status table data to prevent unnecessary recalculations
  const { statusColumns, statusRows } = useMemo(() => {
    const columns: GridColDef[] = [
      {
        field: 'action',
        headerName: 'Action',
        flex: 1,
      },
      {
        field: 'value',
        headerName: 'Value',
        flex: 1,
      },
    ]

    const columnsToShow = [
      'execution_time_ms',
      'num_target_rows_deleted',
      'num_target_rows_inserted',
      'num_target_rows_updated',
    ]

    const rows = Object.entries(contextUpdateRowsStatus ?? {})
      .filter(([key]) => columnsToShow.includes(key))
      .map(([key, value]) => ({
        id: key,
        action: key,
        value: value ?? 'No change',
      }))

    return { statusColumns: columns, statusRows: rows }
  }, [contextUpdateRowsStatus])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
        height: '100%',
        gap: 3,
        overflowY: 'auto',
      }}
    >
      {isUploadComplete ? (
        /* Show status after upload completes */
        <Fade in={isUploadComplete} timeout={800}>
          <Box width="100%" height="100%">
            <Alert
              icon={<CheckCircleOutlineIcon fontSize="inherit" />}
              severity="success"
              sx={{ mb: 3 }}
            >
              Upload completed successfully!
            </Alert>

            <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
              Upload Summary
            </Typography>

            <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
              <Box sx={{ width: '100%', mb: 3 }}>
                <Table
                  disableRowSelectionOnClick
                  rows={statusRows}
                  columns={statusColumns}
                  showCellVerticalBorder
                  showColumnVerticalBorder
                  initialState={{
                    density: 'compact',
                  }}
                  sx={{
                    '& .MuiDataGrid-cell': {
                      fontSize: '0.9rem',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    },
                  }}
                />
              </Box>
            </Paper>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <LoadingButton
                variant="contained"
                color="primary"
                onClick={handleModalClose}
                size="large"
              >
                Complete
              </LoadingButton>
            </Box>
          </Box>
        </Fade>
      ) : (
        /* Show comparison and upload instructions */
        <>
          {/* Validation warning for missing required columns */}
          {!isValidForUpload && missingRequiredColumns.length > 0 && (
            <Alert severity="error" sx={{ mb: 3, width: '100%' }}>
              <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 1 }}>
                Required columns are missing mapping:
              </Typography>
              <Typography variant="body2" component="div">
                The following required columns must be mapped before upload:
                <Box component="ul" sx={{ mt: 1, mb: 0 }}>
                  {missingRequiredColumns.map((column) => (
                    <li key={column}>
                      <strong>{column}</strong>
                    </li>
                  ))}
                </Box>
              </Typography>
              <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
                Please go back to the Mapping step and ensure all required
                columns are mapped to CSV columns.
              </Typography>
            </Alert>
          )}

          {/* Warning for duplicate predicate combinations */}
          {duplicatePredicates.hasDuplicates && (
            <Alert severity="error" sx={{ mb: 3, width: '100%' }}>
              <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 1 }}>
                Duplicate Rows Detected:
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Your uploaded file contains{' '}
                {duplicatePredicates.duplicates.length} groups of rows with
                identical key columns ({contextPredicate?.join(', ')}). This
                could lead to unexpected behavior when updating data.
              </Typography>
              <Typography variant="body2" component="div">
                Duplicate groups:
                <Box component="ul" sx={{ mt: 1, mb: 0 }}>
                  {duplicatePredicates.duplicates
                    .slice(0, 5)
                    .map((duplicate, index) => (
                      <li key={index}>
                        <strong>{duplicate.key}</strong> ({duplicate.count}{' '}
                        rows)
                      </li>
                    ))}
                  {duplicatePredicates.duplicates.length > 5 && (
                    <li>
                      ... and {duplicatePredicates.duplicates.length - 5} more
                      groups
                    </li>
                  )}
                </Box>
              </Typography>
              <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
                Please review your uploaded file and ensure each combination of
                key columns ({contextPredicate?.join(', ')}) appears only once.
              </Typography>
            </Alert>
          )}

          {/* Warning for empty/null predicate values */}
          {emptyPredicates.hasEmptyPredicates && (
            <Alert severity="error" sx={{ mb: 3, width: '100%' }}>
              <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 1 }}>
                Empty Key Values Detected:
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Your uploaded file contains{' '}
                {emptyPredicates.emptyPredicates.length} rows with empty, null,
                or undefined values in predicate columns (
                {contextPredicate?.join(', ')}). All predicate columns must have
                valid values for data processing.
              </Typography>
              <Typography variant="body2" component="div">
                Rows with empty key values:
                <Box component="ul" sx={{ mt: 1, mb: 0 }}>
                  {emptyPredicates.emptyPredicates
                    .slice(0, 5)
                    .map((emptyRow, index) => (
                      <li key={index}>
                        <strong>Row {emptyRow.rowIndex}</strong>: Empty columns
                        [{emptyRow.emptyColumns.join(', ')}]
                      </li>
                    ))}
                  {emptyPredicates.emptyPredicates.length > 5 && (
                    <li>
                      ... and {emptyPredicates.emptyPredicates.length - 5} more
                      rows
                    </li>
                  )}
                </Box>
              </Typography>
              <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
                Please review your uploaded file and ensure all key columns (
                {contextPredicate?.join(', ')}) have valid, non-empty values in
                every row.
              </Typography>
            </Alert>
          )}

          {/* Warning for fewer rows in uploaded file */}
          {rowCountWarning && (
            <Alert severity="warning" sx={{ mb: 3, width: '100%' }}>
              <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 1 }}>
                Row Count Warning:
              </Typography>
              <Typography variant="body2">{rowCountWarning}</Typography>
              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                Please verify that your uploaded file contains all the expected
                data.
              </Typography>
            </Alert>
          )}

          <Typography variant="h6" color="primary" align="center">
            Changes Summary
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            align="center"
            sx={{ mb: 1 }}
          >
            Below are the differences between your original data and the
            uploaded CSV. Only successfully mapped columns are shown.
          </Typography>

          {transformedDataColumns.length > 0 && (
            <Alert severity="info" sx={{ mb: 3, width: '100%' }}>
              <Typography variant="body2">
                <strong>Columns included in comparison:</strong>{' '}
                {transformedDataColumns.join(', ')}
              </Typography>
              {contextDataConfig &&
                contextDataConfig.optional &&
                contextDataConfig.optional.length > 0 && (
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, fontStyle: 'italic' }}
                  >
                    Unmapped optional columns are excluded from the comparison
                    and final upload.
                  </Typography>
                )}
            </Alert>
          )}

          {/* Table for modified rows */}
          {diffData.length > 0 ? (
            <Box sx={{ width: '100%', height: '400px' }}>
              <Typography
                variant="subtitle1"
                color="primary"
                sx={{ mb: 1, fontWeight: 'medium' }}
              >
                Modified Rows
              </Typography>
              {/* Create a dynamic table for differences */}
              <Table
                disableRowSelectionOnClick
                rows={diffData}
                columns={diffColumns}
                showCellVerticalBorder
                showColumnVerticalBorder
                pagination
                initialState={{
                  density: 'compact',
                  pagination: { paginationModel: { pageSize: 15 } },
                }}
                sx={{
                  '& .MuiDataGrid-cell': {
                    fontSize: '0.9rem',
                  },
                  '& .old-value-cell': {
                    backgroundColor: '#ffebee',
                  },
                  '& .new-value-cell': {
                    backgroundColor: '#e8f5e9',
                  },
                  '& .predicate-cell': {
                    backgroundColor: '#e3f2fd', // Light blue background for predicate/ID columns
                    fontWeight: 'bold',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  },
                }}
              />
            </Box>
          ) : (
            <Alert severity="info" sx={{ mb: 3, width: '100%' }}>
              No modified rows found between the original and uploaded file
              data.
            </Alert>
          )}

          {/* Table for newly added rows */}
          {addedData.length > 0 ? (
            <Box sx={{ width: '100%', height: '400px', mt: 3 }}>
              <Typography
                variant="subtitle1"
                color="primary"
                sx={{ mb: 1, fontWeight: 'medium' }}
              >
                Newly Added Rows
              </Typography>
              <Table
                disableRowSelectionOnClick
                rows={addedData}
                columns={addedColumnsData}
                showCellVerticalBorder
                showColumnVerticalBorder
                pagination
                initialState={{
                  density: 'compact',
                  pagination: { paginationModel: { pageSize: 15 } },
                }}
                sx={{
                  '& .MuiDataGrid-cell': {
                    fontSize: '0.9rem',
                  },
                  '& .added-value-cell': {
                    backgroundColor: '#e8f5e9', // Light green background for added values
                  },
                  '& .predicate-cell': {
                    backgroundColor: '#e3f2fd', // Light blue background for predicate/ID columns
                    fontWeight: 'bold',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  },
                }}
              />
            </Box>
          ) : (
            <Alert severity="info" sx={{ mt: 3, mb: 3, width: '100%' }}>
              No new rows found in the uploaded file.
            </Alert>
          )}

          <Typography
            variant="h6"
            color={isValidForUpload ? 'textSecondary' : 'error'}
            align="center"
            sx={{ mt: 2 }}
          >
            {isValidForUpload
              ? 'Ready to upload your CSV data?'
              : 'Upload blocked - mapping required'}
          </Typography>
          <Typography variant="body1" color="textSecondary" align="center">
            {isValidForUpload
              ? "Make sure you've reviewed your mapping in the previous tab and the changes above."
              : 'Please fix the mapping issues above before proceeding with upload.'}
          </Typography>

          {/* Only showing cancel button since upload button is now in the stepper */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
              mt: 4,
            }}
          >
            <LoadingButton
              variant="outlined"
              onClick={handleModalClose}
              size="large"
            >
              Cancel
            </LoadingButton>
          </Box>
        </>
      )}
    </Box>
  )
}

export default UploadTabContent
